var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CrossOriginWorkerMaker as Worker } from "./cross-origin-worker";
import { MessagePortWebSocket } from "./messageportwebsocket";
import { PromiseDelegate } from "./promise-delegate";
export class WorkerProxy extends EventTarget {
    constructor(options) {
        super();
        this.firstRunPromiseDelegate = new PromiseDelegate();
        console.debug("WorkerProxy.constructor(): Create a new worker.");
        // Loading a worker here relies on Vite's support for WebWorkers (https://vitejs.dev/guide/features.html#web-workers),
        // assuming that this module is imported from the Gradio frontend (`@gradio/app`), which is bundled with Vite.
        // HACK: Use `CrossOriginWorkerMaker` imported as `Worker` here.
        // Read the comment in `cross-origin-worker.ts` for the detail.
        const workerMaker = new Worker(new URL("./webworker.js", import.meta.url));
        this.worker = workerMaker.worker;
        this.worker.onmessage = (e) => {
            this._processWorkerMessage(e.data);
        };
        this.postMessageAsync({
            type: "init",
            data: {
                gradioWheelUrl: options.gradioWheelUrl,
                gradioClientWheelUrl: options.gradioClientWheelUrl,
                files: options.files,
                requirements: options.requirements
            }
        })
            .then(() => {
            console.debug("WorkerProxy.constructor(): Initialization is done.");
        })
            .catch((error) => {
            console.error("WorkerProxy.constructor(): Initialization failed.", error);
            this.dispatchEvent(new CustomEvent("initialization-error", {
                detail: error
            }));
        });
    }
    runPythonCode(code) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.postMessageAsync({
                type: "run-python-code",
                data: {
                    code
                }
            });
            this.firstRunPromiseDelegate.resolve();
        });
    }
    runPythonFile(path) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.postMessageAsync({
                type: "run-python-file",
                data: {
                    path
                }
            });
            this.firstRunPromiseDelegate.resolve();
        });
    }
    // A wrapper for this.worker.postMessage(). Unlike that function, which
    // returns void immediately, this function returns a promise, which resolves
    // when a ReplyMessage is received from the worker.
    // The original implementation is in https://github.com/rstudio/shinylive/blob/v0.1.2/src/pyodide-proxy.ts#L404-L418
    postMessageAsync(msg) {
        return new Promise((resolve, reject) => {
            const channel = new MessageChannel();
            channel.port1.onmessage = (e) => {
                channel.port1.close();
                const msg = e.data;
                if (msg.type === "reply:error") {
                    reject(msg.error);
                    return;
                }
                resolve(msg.data);
            };
            this.worker.postMessage(msg, [channel.port2]);
        });
    }
    _processWorkerMessage(msg) {
        switch (msg.type) {
            case "progress-update": {
                this.dispatchEvent(new CustomEvent("progress-update", {
                    detail: msg.data.log
                }));
                break;
            }
        }
    }
    httpRequest(request) {
        return __awaiter(this, void 0, void 0, function* () {
            // Wait for the first run to be done
            // to avoid the "Gradio app has not been launched." error
            // in case running the code takes long time.
            // Ref: https://github.com/gradio-app/gradio/issues/5957
            yield this.firstRunPromiseDelegate.promise;
            console.debug("WorkerProxy.httpRequest()", request);
            const result = yield this.postMessageAsync({
                type: "http-request",
                data: {
                    request
                }
            });
            const response = result.response;
            if (Math.floor(response.status / 100) !== 2) {
                let bodyText;
                let bodyJson;
                try {
                    bodyText = new TextDecoder().decode(response.body);
                }
                catch (e) {
                    bodyText = "(failed to decode body)";
                }
                try {
                    bodyJson = JSON.parse(bodyText);
                }
                catch (e) {
                    bodyJson = "(failed to parse body as JSON)";
                }
                console.error("Wasm HTTP error", {
                    request,
                    response,
                    bodyText,
                    bodyJson
                });
            }
            return response;
        });
    }
    openWebSocket(path) {
        const channel = new MessageChannel();
        const msg = {
            type: "websocket",
            data: {
                path
            }
        };
        this.worker.postMessage(msg, [channel.port2]);
        return new MessagePortWebSocket(channel.port1);
    }
    writeFile(path, data, opts) {
        return this.postMessageAsync({
            type: "file:write",
            data: {
                path,
                data,
                opts
            }
        });
    }
    renameFile(oldPath, newPath) {
        return this.postMessageAsync({
            type: "file:rename",
            data: {
                oldPath,
                newPath
            }
        });
    }
    unlink(path) {
        return this.postMessageAsync({
            type: "file:unlink",
            data: {
                path
            }
        });
    }
    install(requirements) {
        return this.postMessageAsync({
            type: "install",
            data: {
                requirements
            }
        });
    }
    terminate() {
        this.worker.terminate();
    }
}
